import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import {
  AdditionalFieldSpec,
  AdditionalFieldValue,
  CombinedFilingData,
  DocumentInfo,
  FieldCategory,
  ParticipantFieldSpec,
  RequestDocument,
  RequestDocumentParticipantViewModel,
  RequestParticipantViewModel,
} from '@fsx/fsx-shared';
import {
  DropdownOption,
  FilesUploadedEventParams,
  FormControlWithoutModel,
  ParticipantTableComponent,
  SelectionFieldType,
} from '@fsx/ui-components';
import { DocumentsGridRow } from '../documents-grid/documents-grid.model';
import { FsxReferenceResolver } from '../../shared/resolvers/list-reference.resolver';

@Component({
  selector: 'fsx-participant-field',
  templateUrl: './participant-field.component.html',
  styleUrls: ['./participant-field.component.scss'],
})
export class ParticipantFieldComponent implements OnChanges {
  @Input() inputDropdownOptions: DropdownOption<void>[] = [];
  @Input() combinedFilingData!: CombinedFilingData;
  @Input() caption!: string;
  @Input() documentsGridRow!: DocumentsGridRow;
  @Input() selectedParticipants: RequestParticipantViewModel[] = [];
  @Input() participantFieldSpec!: ParticipantFieldSpec;
  @Input() width!: string;
  @Input() resolver!: FsxReferenceResolver;
  @Input() addlFieldSpec!: AdditionalFieldSpec[];
  @Input() fileUploadDocumentInfos!: DocumentInfo[];
  @Input()
  targetRequestDocumentParticipant!: RequestDocumentParticipantViewModel;
  @Output() selectedParticipantEvent =
    new EventEmitter<RequestParticipantViewModel>();
  @Output() removeParticipantEvent =
    new EventEmitter<RequestParticipantViewModel>();
  @Output() formControlEmitter = new EventEmitter<FormControlWithoutModel>(
    true
  );
  @Output() filesUploadedFromAdditionalFieldEvent =
    new EventEmitter<FilesUploadedEventParams>();
  @Output() fileRemovedFromAdditionalFieldEvent =
    new EventEmitter<DocumentInfo>();

  @ViewChildren('participantsField')
  participantsFields!: QueryList<ParticipantTableComponent>;

  filteredDropdownOptions: DropdownOption<void>[] = [];
  public fieldType = FieldCategory;
  public selectionType = SelectionFieldType.StringSelectionFieldDefinition;
  public additionalFieldValues: AdditionalFieldValue[] = [];
  minRequired!: number;
  maxAllowed!: number;
  additionalFieldSpecs!: AdditionalFieldSpec[] | null;
  required!: boolean;

  ngOnChanges(): void {
    this.setFilteredDropdownOptions();
    this.minRequired =
      this.participantFieldSpec?.participantFieldDefinition?.minRequired || 0;
    this.maxAllowed =
      this.participantFieldSpec?.participantFieldDefinition?.maxAllowed || 99;
    this.additionalFieldSpecs =
      this.participantFieldSpec?.participantFieldDefinition?.additionalFields ||
      null;

    this.required = this.selectedParticipants.length < this.minRequired;
    this.additionalFieldValues =
      this.targetRequestDocumentParticipant?.additionalFieldValues || [];
  }

  private setFilteredDropdownOptions() {
    this.filteredDropdownOptions = this.selectedParticipants.reduce(
      (acc: DropdownOption<void>[], cur: RequestParticipantViewModel) => {
        return acc.filter((aaa: DropdownOption<void>) => {
          return aaa.name !== cur.name;
        });
      },
      [...this.inputDropdownOptions]
    );
  }

  onParticipantSelected(params: {
    value: string;
    requestDocument: RequestDocument;
    documentIndex: number;
  }) {
    const { caseRequest } = this.combinedFilingData!;
    const selectedParticipant: RequestParticipantViewModel | undefined =
      caseRequest.participants?.find((p) => p.name === params.value);
    if (params.requestDocument.cases) {
      this.additionalFieldValues =
        params.requestDocument.cases[0].additionalFieldValues ?? [];
    }

    if (selectedParticipant) {
      this.selectedParticipantEvent.emit(selectedParticipant);
    }
  }

  clearParticipantEventHandler(
    participantToClear: RequestParticipantViewModel
  ) {
    this.removeParticipantEvent.emit(participantToClear);
  }

  participantformControl($event: FormControlWithoutModel) {
    this.formControlEmitter.emit($event);
  }

  setAdditionalFieldValues(addlFields: AdditionalFieldValue[]) {
    let { caseRequest } = this.combinedFilingData!;
    this.targetRequestDocumentParticipant.additionalFieldValues = addlFields;
    this.resolver.updateCaseRequestPut(
      caseRequest,
      this.combinedFilingData?.filing.id ?? ''
    );
  }

  public filesUploadedFromAdditionalFieldEventHandler(
    params: FilesUploadedEventParams
  ) {
    this.filesUploadedFromAdditionalFieldEvent.emit(params);
  }

  public fileRemovedFromAdditionalFieldEventHandler(
    documentInfo: DocumentInfo
  ) {
    this.fileRemovedFromAdditionalFieldEvent.emit(documentInfo);
  }

  public validate(): void {
    if (this.participantsFields) {
      this.participantsFields.toArray().forEach((fld) => fld.validate());
    }
  }
}
